import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  useBlocker,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { AxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import { Editor } from "tldraw";

import { ErrorBanner } from "@/components/Callout";
import {
  customSortSettingsOf,
  gymSettingWrites,
  lensListHasGym,
} from "@/components/canvas-editor/gym-settings";
import { defaultFeedVersion } from "@/components/canvas-editor/utils";
import { FeedAvatarInput } from "@/components/FeedAvatarInput";
import { FeedDescriptionInput } from "@/components/FeedDescriptionInput";
import { FeedEditor, FeedEditorHandle } from "@/components/FeedEditor";
import { FeedForm } from "@/components/FeedForm";
import { FeedManifestDeliverySettings } from "@/components/FeedManifestDeliverySettings";
import { FeedPublicSwitch } from "@/components/FeedPublicSwitch";
import { FeedSlugInput } from "@/components/FeedSlugInput";
import { FeedTitleInput } from "@/components/FeedTitleInput";
import { Button, ButtonLink } from "@/components/ui/button";
import {
  createFeed,
  getPublicFeedQueryOptions,
  getUserCustomNodesQueryOptions,
  myFeedsQueryOptions,
  publishFeed,
  searchCustomNodesQueryOptions,
  setFeedPublic,
  setAlgoSetting,
} from "@/lib/api";
import { useLabelerPublishGate } from "@/lib/labelers";
import { FeedVersion } from "@/lib/types";

export const Route = createFileRoute("/app/_sidebar-layout/create/")({
  component: RouteComponent,
  validateSearch: (
    search: Record<string, unknown>,
  ): { remix?: number; generate?: boolean } => {
    // validate and parse the search params into a typed state
    return {
      remix: search?.remix ? parseInt(search?.remix as string) : undefined,
      generate: search?.generate ? true : undefined,
    };
  },
  loader: async ({ location, context: { queryClient } }) => {
    void queryClient.ensureQueryData(getUserCustomNodesQueryOptions());
    void queryClient.prefetchInfiniteQuery(
      searchCustomNodesQueryOptions({ query: "", favorited_only: true }),
    );

    await Promise.all([
      "remix" in location.search
        ? queryClient.ensureQueryData(
            getPublicFeedQueryOptions(location.search.remix as number),
          )
        : undefined,
      queryClient.ensureQueryData(myFeedsQueryOptions({ page: 1 })),
    ]);
  },
  head: () => ({ meta: [{ title: "Create Feed" }] }),
});

function RouteComponent() {
  // Publish the feed

  const location = useLocation();
  const { data: loaderData } = location.search.remix
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useSuspenseQuery(getPublicFeedQueryOptions(location.search.remix))
    : { data: undefined };
  const { data: feeds } = useSuspenseQuery(myFeedsQueryOptions({ page: 1 }));
  const { generate } = Route.useSearch();
  const [phase, setPhase] = useState<"manual" | "generate">(
    loaderData || !generate ? "manual" : "generate",
  );
  const [feedVersion, setFeedVersion] = useState<
    Omit<FeedVersion, "created_at"> | undefined
  >(
    loaderData
      ? {
          is_legacy: loaderData.draft_algorithm_manifest.is_legacy,
          manifest: loaderData.draft_algorithm_manifest.manifest,
          order: loaderData.draft_algorithm_manifest.order,
          custom_sort_settings:
            loaderData.draft_algorithm_manifest.custom_sort_settings,
          metadata: loaderData.draft_algorithm_manifest.metadata,
          emit: loaderData.draft_algorithm_manifest.emit,
          onEmitNull: loaderData.draft_algorithm_manifest.onEmitNull,
        }
      : defaultFeedVersion,
  );
  const navigate = useNavigate({ from: "/app/create" });
  const feedEditorRef = useRef<FeedEditorHandle>(null!);
  const editorRef = useRef<Editor>(null!);
  const shouldBlock = useRef(true);
  const [isPublishing, setIsPublishing] = useState(false);
  // See `labelerPublishBlockReason`: a labeler still syncing blocks Publish, not Save.
  const labelGate = useLabelerPublishGate(feedVersion?.manifest?.filter);
  const onSubmit = useMutation({
    mutationFn: async (data: FormData) => {
      if (labelGate.saveBlocked) {
        throw new Error(labelGate.saveReason);
      }
      if (isPublishing && labelGate.blocked) {
        throw new Error(labelGate.reason);
      }

      const { algorithm } = await createFeed(data);

      // A new feed born with a gym layer starts with the gym on. See gym-settings.ts.
      for (const write of gymSettingWrites(
        undefined,
        false,
        lensListHasGym(customSortSettingsOf(data)),
      )) {
        await setAlgoSetting(algorithm.id, write);
      }

      if (data.get("public") === "on") {
        await setFeedPublic(algorithm.id, true);
      }

      if (isPublishing) {
        await publishFeed(algorithm.id);
      }

      return algorithm;
    },
    onSuccess: async (algorithm) => {
      shouldBlock.current = false;
      await navigate({
        to: "/app/feed/$feedId/view",
        params: { feedId: `${algorithm.id}` },
      });
    },
    onError: (error: AxiosError) => console.error(error),
  });

  const shouldTour = feeds.user_algos.length === 0;
  useEffect(() => {
    if (phase === "manual" && shouldTour) {
      const timeout = setTimeout(() => {
        document.querySelector<HTMLElement>("#start-tour")?.click();
      }, 600);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [phase, shouldTour]);

  useBlocker({
    shouldBlockFn: (event) => {
      if (event.action === "REPLACE") {
        return false;
      }

      if (shouldBlock.current) {
        const shouldNav = confirm(
          "Are you sure you want to leave? This will discard all your changes.",
        );
        return !shouldNav;
      }

      return false;
    },
  });

  return (
    <FeedForm
      isCreate={true}
      onSubmit={onSubmit.mutate}
      feedVersion={feedVersion}
      feedEditorRef={feedEditorRef}
      editorRef={editorRef}
    >
      {onSubmit.error && <ErrorBanner error={onSubmit.error} />}
      <div className="flex gap-4 border-b-2 border-slate-7 dark:border-slatedark-7 pb-4 self-stretch">
        <FeedAvatarInput />
        <FeedTitleInput className="h-full" />
      </div>

      <FeedPublicSwitch />
      <FeedSlugInput />
      <FeedDescriptionInput />
      <FeedManifestDeliverySettings
        emit={feedVersion?.emit}
        onEmitNull={feedVersion?.onEmitNull}
        onEmitChange={(emit) =>
          setFeedVersion((currentFeedVersion) =>
            currentFeedVersion ? { ...currentFeedVersion, emit } : currentFeedVersion,
          )
        }
        onEmitNullChange={(onEmitNull) =>
          setFeedVersion((currentFeedVersion) =>
            currentFeedVersion
              ? { ...currentFeedVersion, onEmitNull }
              : currentFeedVersion,
          )
        }
      />
      <FeedEditor
        handle={feedEditorRef}
        editorRef={editorRef}
        templateMode={phase === "generate"}
        onTemplateModeChange={(newMode) => {
          setPhase(newMode ? "generate" : "manual");
        }}
        feedVersion={feedVersion}
        onChange={(newVersion) =>
          setFeedVersion((currentFeedVersion) => ({
            ...newVersion,
            emit: newVersion.emit ?? currentFeedVersion?.emit,
            onEmitNull: newVersion.onEmitNull ?? currentFeedVersion?.onEmitNull,
          }))
        }
        fullscreenActions={
          <Button
            size="sm"
            isLoading={onSubmit.isPending}
            disabled={labelGate.saveBlocked}
            type="submit"
          >
            Create
          </Button>
        }
        centerContent={true}
      />

      {phase === "manual" && (
        <div className="flex justify-between gap-4">
          <div className="flex-1" />
          <div className="flex gap-2">
            <ButtonLink size="lg" variant="ghost" to="/app">
              Cancel
            </ButtonLink>
            <Button
              size="lg"
              variant="outline"
              isLoading={onSubmit.isPending && !isPublishing}
              disabled={labelGate.saveBlocked}
              type="submit"
            >
              Save Draft
            </Button>
            {labelGate.blocked && (
              <span
                className={
                labelGate.saveBlocked
                  ? "self-center text-sm text-red-11 dark:text-reddark-11 max-w-md"
                  : "self-center text-sm text-amber-11 dark:text-amberdark-11 max-w-md"
              }
                role={labelGate.saveBlocked ? "alert" : "status"}
              >
                {labelGate.reason}
              </span>
            )}
            <Button
              size="lg"
              isLoading={onSubmit.isPending && isPublishing}
              disabled={labelGate.blocked}
              type="submit"
              onClick={() => setIsPublishing(true)}
            >
              Publish
            </Button>
          </div>
        </div>
      )}
    </FeedForm>
  );
}
